import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants, EventId } from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import type {
  BaseballFixture,
  BaseballInningScore,
  BaseballPeriod
} from '@ps/web-shared-generated-graphql/sports';
import React from 'react';

import { EventTypeId } from '@swf/sports/types/eventType';
import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventId: EventId;
  eventTime: string;
  baseballFixture?: BaseballFixture;
}

const standardPeriods = [
  'INNING_1',
  'INNING_2',
  'INNING_3',
  'INNING_4',
  'INNING_5',
  'INNING_6',
  'INNING_7',
  'INNING_8',
  'INNING_9'
] as const satisfies BaseballPeriod[];

const isBaseballInningScore = (
  inningScore: BaseballInningScore | null
): inningScore is BaseballInningScore =>
  notEmpty(inningScore) && notEmpty(inningScore.period) && notEmpty(inningScore.score);

const getScorePerInning = (
  scorePerInning: BaseballFixture['scorePerInning']
): BaseballInningScore[] => {
  const standardPeriodScores = standardPeriods.map(
    (period) => scorePerInning?.find((spi) => spi?.period === period) ?? { period }
  );

  const additionalPeriodScores =
    scorePerInning?.filter(
      (spi) => !standardPeriodScores.find((sp) => sp.period === spi?.period)
    ) ?? [];

  return [...standardPeriodScores, ...additionalPeriodScores.filter(isBaseballInningScore)];
};

const getParticipantScore = (
  participant: EnrichedParticipants[number],
  score: BaseballFixture['score'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const Baseball: React.FC<Props> = ({
  enrichedParticipants,
  baseballFixture,
  eventId,
  eventTime
}) => {
  const [participantA, participantB] = enrichedParticipants ?? [];

  const currentScore = participantA.type && participantB.type ? baseballFixture?.score : null;
  const scoreByInning =
    participantA.type && participantB.type ? baseballFixture?.scorePerInning : null;

  const hasInningScores = notEmpty(scoreByInning);
  const inningScores = getScorePerInning(scoreByInning);

  return (
    <ScoreboardWrapper eventId={eventId} eventTime={eventTime} eventTypeId={EventTypeId.BASEBALL}>
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant name={participantA.participant} />
          <ScoreboardParticipant name={participantB.participant} />
        </ScoreboardParticipants>
        <ScoreboardScores>
          {hasInningScores
            ? inningScores.map((inningScore) => (
                <ScoreboardScore
                  key={inningScore.period}
                  score={[
                    getParticipantScore(participantA, inningScore.score) ?? null,
                    getParticipantScore(participantB, inningScore.score) ?? null
                  ]}
                />
              ))
            : null}
          <ScoreboardScore
            score={[
              getParticipantScore(participantA, currentScore) ?? null,
              getParticipantScore(participantB, currentScore) ?? null
            ]}
            scoreCharacterLength={getScoreCharacterLength(EventTypeId.BASEBALL, [
              currentScore?.home ?? 0,
              currentScore?.away ?? 0
            ])}
          />
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default Baseball;
